import { ReactNode, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { DEFAULT_COLORS } from "@/lib/const";
import { cn } from "@/lib/utils";
import { CardColor } from "@/types/v2";

interface CardForceEffectDialogProps {
  action: "flash" | "wiggle";
  defaultColor: string;
  cardColors: CardColor[];
  onSelect: (color: string) => void;
  trigger?: ReactNode;
  externalTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ACTION_LABEL: Record<"flash" | "wiggle", string> = {
  flash: "Flash Card",
  wiggle: "Wiggle Card",
};

export const CardForceEffectDialog = ({
  action,
  defaultColor,
  cardColors,
  onSelect,
  trigger,
  externalTrigger,
  open,
  onOpenChange,
}: CardForceEffectDialogProps) => {
  const [isOpen, setIsOpen] = useState(open ?? false);
  const defaultTitleById = useMemo(
    () => new Map(DEFAULT_COLORS.map((c) => [c.id, c.title])),
    []
  );

  // Sync local state with the controlled `open` prop
  useEffect(() => {
    if (typeof open === "boolean") {
      setIsOpen(open);
    }
  }, [open]);

  // Handle the dialog's open state changes
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen); // Notify parent if `onOpenChange` is provided
  };

  const handleSelectColor = (color: string) => {
    onSelect(color);
    handleOpenChange(false);
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen}>
      {trigger && !externalTrigger && (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      )}
      <DialogContent className="w-[706px] max-w-full">
        <DialogHeader>
          <DialogTitle>{ACTION_LABEL[action]}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-4 justify-items-center gap-[15px] py-2">
          {cardColors.map((colorItem) => {
            const isDefaultTitle =
              defaultTitleById.get(colorItem.id) === colorItem.title;
            return (
              <button
                key={colorItem.id}
                type="button"
                className={cn(
                  "flex h-[40px] w-[150px] cursor-pointer items-center justify-center rounded-sm border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  colorItem.color === defaultColor &&
                    "ring-2 ring-ring ring-offset-2"
                )}
                style={{
                  backgroundColor: colorItem.color,
                  borderColor: "#35383B",
                }}
                aria-label={colorItem.title}
                onClick={() => handleSelectColor(colorItem.color)}
              >
                {!isDefaultTitle && (
                  <span className="text-sm font-medium text-black">
                    {colorItem.title}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant={"secondary"} onClick={handleCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
